import DeliveryTracking from "./DeliveryTracking";
import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import { Truck, Package, Clock, CheckCircle, ChevronRight } from "lucide-react";
import Toast from "./Toast";
import DeliveryDetailModal from "./DeliveryDetailModal";
import LiveTrackingModal from "./LiveTrackingModal";

interface DeliveryViewProps {
  deliveries: {
    batchId: string;
    vehicleNumber: string;
    driverName: string;
    departureTime: string;
    arrivalTime: string;
    distance: string;
    temperature: number;
    status: "in-transit" | "delivered" | "pending";
    imageUrl: string;
  }[];
}

export default function DeliveryView({ deliveries }: DeliveryViewProps) {
  const [toast, setToast] = useState<{ message: string; type: "success" | "info" } | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "in-transit" | "delivered" | "pending">("all");
  const [selectedDelivery, setSelectedDelivery] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);

  const filteredDeliveries = filterStatus === "all" 
    ? deliveries 
    : deliveries.filter(d => d.status === filterStatus);

  const handleCallDriver = (driverName: string, vehicleNumber: string) => {
    setToast({ message: `Calling ${driverName} (${vehicleNumber})...`, type: "info" });
    // Simulate phone call action
    setTimeout(() => {
      setToast({ message: `Connected to ${driverName}'s phone`, type: "success" });
    }, 1500);
  };

  const handleViewRoute = (delivery: any) => {
    setSelectedDelivery(delivery);
    setIsTrackingModalOpen(true);
  };

  const handleViewDetails = (delivery: any) => {
    setSelectedDelivery(delivery);
    setIsDetailModalOpen(true);
  };

  const statusConfig = {
    "in-transit": { 
      icon: Truck,
      bg: "bg-blue-50",
      text: "text-blue-400",
      label: "In Transit",
      count: deliveries.filter(d => d.status === "in-transit").length
    },
    "delivered": { 
      icon: CheckCircle,
      bg: "bg-green-50",
      text: "text-green-600",
      label: "Delivered",
      count: deliveries.filter(d => d.status === "delivered").length
    },
    "pending": { 
      icon: Clock,
      bg: "bg-amber-50",
      text: "text-amber-600",
      label: "Pending",
      count: deliveries.filter(d => d.status === "pending").length
    },
  };

  return (
    <div className="pb-32 bg-[#F9FAFB] min-h-screen">
      {/* Navy Blue Header */}
      <div className="bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white px-5 pt-6 pb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-white mb-1" style={{ fontSize: "32px", fontWeight: "700", letterSpacing: "-0.025em" }}>
              Delivery Tracking
            </h1>
            <p className="text-white/60" style={{ fontSize: "13px", letterSpacing: "0.01em" }}>Real-time monitoring</p>
          </div>
        </div>

        {/* Stats Cards in Header */}
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(statusConfig).map(([status, config]) => {
            const Icon = config.icon;
            const isActive = filterStatus === status;
            return (
              <motion.button
                key={status}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setFilterStatus(filterStatus === status ? "all" : status as any)}
                className={`p-3 rounded-xl transition-all ${
                  isActive 
                    ? "bg-white text-slate-900" 
                    : "bg-white/10 backdrop-blur-xl text-white border border-white/20"
                }`}
              >
                <Icon className={`w-5 h-5 mb-2 ${isActive ? "text-blue-500" : "text-white"}`} />
                <div className={`mb-0.5 ${isActive ? "text-slate-900" : "text-white"}`} style={{ fontSize: "20px", fontWeight: "700", letterSpacing: "-0.02em" }}>
                  {config.count}
                </div>
                <div className={isActive ? "text-slate-500" : "text-white/60"} style={{ fontSize: "11px", letterSpacing: "0.02em" }}>
                  {config.label}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="p-5 space-y-3 -mt-4">
        
        {/* Delivery List */}
        <AnimatePresence mode="wait">
          <div className="space-y-3">
            {filteredDeliveries.map((delivery, index) => {
              const config = statusConfig[delivery.status];
              const StatusIcon = config.icon;

              return (
                <motion.div
                  key={delivery.batchId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  className="bg-white rounded-2xl overflow-hidden"
                  style={{ boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)" }}
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-[#0A0F1C] mb-1" style={{ fontSize: "15px", fontWeight: "700", letterSpacing: "-0.01em" }}>{delivery.batchId}</div>
                        <div className="text-[#1B2A5B]" style={{ fontSize: "13px", fontWeight: "600" }}>{delivery.vehicleNumber}</div>
                      </div>
                      <div 
                        className={`px-3 py-1.5 rounded-full flex items-center gap-1.5`}
                        style={{
                          backgroundColor: delivery.status === "in-transit" ? "#10B98108" : delivery.status === "delivered" ? "#05966908" : "#34D39908",
                          color: delivery.status === "in-transit" ? "#10B981" : delivery.status === "delivered" ? "#059669" : "#34D399"
                        }}
                      >
                        <StatusIcon className="w-3.5 h-3.5" />
                        <span style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.05em", textTransform: "uppercase" }}>{config.label}</span>
                      </div>
                    </div>

                    <div className="bg-[#F9FAFB] rounded-xl p-4 mb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-[#717182] mb-1" style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.02em", textTransform: "uppercase" }}>Driver</div>
                          <div className="text-[#0A0F1C]" style={{ fontSize: "14px", fontWeight: "700" }}>{delivery.driverName}</div>
                        </div>
                        <div>
                          <div className="text-[#717182] mb-1" style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.02em", textTransform: "uppercase" }}>Distance</div>
                          <div className="text-[#0A0F1C]" style={{ fontSize: "14px", fontWeight: "700" }}>{delivery.distance}</div>
                        </div>
                        <div>
                          <div className="text-[#717182] mb-1" style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.02em", textTransform: "uppercase" }}>Departure</div>
                          <div className="text-[#0A0F1C]" style={{ fontSize: "14px", fontWeight: "700" }}>{delivery.departureTime}</div>
                        </div>
                        <div>
                          <div className="text-[#717182] mb-1" style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.02em", textTransform: "uppercase" }}>ETA</div>
                          <div className="text-[#0A0F1C]" style={{ fontSize: "14px", fontWeight: "700" }}>{delivery.arrivalTime}</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleCallDriver(delivery.driverName, delivery.vehicleNumber)}
                        className="py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: "#F9FAFB",
                          color: "#1B2A5B",
                          fontSize: "13px",
                          fontWeight: "600"
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.5 11.3V13.3C14.5 14.2 13.8 14.9 12.9 14.9C6.1 14.4 1.5 9.8 1 3C1 2.1 1.7 1.4 2.6 1.4H4.6C5.4 1.4 6.1 2 6.2 2.8C6.3 3.6 6.5 4.4 6.8 5.1C7 5.7 6.8 6.4 6.4 6.8L5.3 7.9C6.5 10.1 8.3 11.9 10.5 13.1L11.6 12C12 11.6 12.7 11.4 13.3 11.6C14 11.9 14.8 12.1 15.6 12.2C16.4 12.3 17 13 17 13.8Z" fill="currentColor"/>
                        </svg>
                        Call Driver
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleViewRoute(delivery)}
                        className="py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: "#3B82F6",
                          color: "white",
                          fontSize: "13px",
                          fontWeight: "600",
                          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)"
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 2C6.2 2 4 4.2 4 7C4 10.5 9 16 9 16C9 16 14 10.5 14 7C14 4.2 11.8 2 9 2ZM9 9C7.9 9 7 8.1 7 7C7 5.9 7.9 5 9 5C10.1 5 11 5.9 11 7C11 8.1 10.1 9 9 9Z" fill="currentColor"/>
                        </svg>
                        Track Live
                      </motion.button>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleViewDetails(delivery)}
                      className="w-full mt-3 py-2.5 rounded-xl transition-all text-center flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: "#F9FAFB",
                        color: "#1B2A5B",
                        fontSize: "13px",
                        fontWeight: "600"
                      }}
                    >
                      View Details
                      <ChevronRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </AnimatePresence>

        {filteredDeliveries.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-blue-500" />
            </div>
            <p className="text-slate-900 mb-2" style={{ fontSize: "16px", fontWeight: "600" }}>No deliveries found</p>
            <p className="text-slate-500">Try adjusting your filters</p>
            <button 
              onClick={() => setFilterStatus("all")}
              className="mt-4 px-6 py-2.5 bg-blue-400 hover:bg-blue-500 text-white rounded-xl transition-colors font-semibold"
            >
              View All Deliveries
            </button>
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <DeliveryDetailModal 
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        delivery={selectedDelivery}
        onCallDriver={() => selectedDelivery && handleCallDriver(selectedDelivery.driverName, selectedDelivery.vehicleNumber)}
        onTrackLive={() => selectedDelivery && handleViewRoute(selectedDelivery)}
      />
      
      <LiveTrackingModal 
        isOpen={isTrackingModalOpen}
        onClose={() => setIsTrackingModalOpen(false)}
        delivery={selectedDelivery}
        onCallDriver={() => selectedDelivery && handleCallDriver(selectedDelivery.driverName, selectedDelivery.vehicleNumber)}
      />
    </div>
  );
}